import React from 'react';
import { Check } from 'lucide-react';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { cn } from '../../../lib/utils';
import { isValidAmount } from './eligible';
import { editableComment, overridePeriodNote, overrideSuggestion } from './issue';
import { FINAL } from './resultCardView';

const MAX_WAGERING = 100;
export const MAX_COMMENT_LENGTH = 1500; // the backend allows 2000 with the exception reason in front
export const MAX_REASON_LENGTH = 400;

const EDITABLE = ['eligible', 'granted', 'manual', 'queued'];

// parseWagering accepts a whole multiplier 0–MAX_WAGERING; anything else clears it.
const parseWagering = (raw) => {
  const n = Number(raw);
  return raw !== '' && Number.isInteger(n) && n >= 0 && n <= MAX_WAGERING ? n : null;
};

// commentOf: the comment being edited, else the saved one.
export const commentOf = (ui, check) => (ui.comment ?? editableComment(check.issued));

function Strip({ warning, children, className }) {
  return (
    <div className={cn('flex flex-wrap items-center gap-3 border-t px-6 py-3.5', warning ? 'border-warning/30 bg-warning/5' : 'border-white/10', className)}>
      {children}
    </div>
  );
}

// WageringLine: the wagering the tags decided, the tag behind it and the amount to wager.
function WageringLine({ wagering }) {
  const applied = wagering.matches.filter((m) => !m.overridden);
  return (
    <Strip>
      <span className="text-sm font-medium text-ink-dim">Wagering</span>
      <span className="font-display text-lg font-black text-ink">{wagering.value}x</span>
      {applied.length > 0 && <span className="text-sm font-medium text-ink-dim">from</span>}
      {applied.map((m) => (
        <span key={m.tag} className="rounded-md border border-positive/30 bg-positive/10 px-2 py-0.5 text-xs font-bold text-positive">
          {m.tag} → {m.wagering}x
        </span>
      ))}
      <span className="flex-1" />
      {wagering.toWager && <span className="text-sm font-bold text-ink">= {wagering.toWager} <span className="font-medium text-ink-dim">to wager</span></span>}
    </Strip>
  );
}

function NumberField({ id, label, value, onChange, placeholder, suffix, width = 'w-20', invalid }) {
  return (
    <div className="flex items-center gap-2">
      <label htmlFor={id} className="text-sm font-bold text-warning">{label}</label>
      <Input
        id={id} value={value} onChange={(e) => onChange(e.target.value.trim())} placeholder={placeholder}
        className={cn('h-10 border-warning/50 bg-surface font-bold', width, invalid && 'border-negative')}
      />
      {suffix && <span className="text-sm font-medium text-ink-dim">{suffix}</span>}
    </div>
  );
}

// InputsStrip: what the manager fills in before issuing — the wagering (tag choices on a manual
// check, else a number) and the amount when it could not be converted or is being adjusted.
function InputsStrip({ check, view, ui, patchUI, overrides, setOverrides }) {
  // Shown while the tags / today's rate leave it open, so the field stays as the manager types.
  const needWagering = Boolean(view.wagering?.missing);
  const showAmount = !check.proposal.issue_amount || ui.adjusting;
  if (!needWagering && !showAmount) return null;
  // conflicting tags (manual check, or the exception band with a conflict): pick one of them
  const choices = view.wagering.matches.filter((m) => !m.overridden);
  const currency = check.proposal.issue_amount ? check.proposal.issue_currency : check.proposal.basis_currency;
  const amountDraft = ui.amountDraft ?? overrides.amount ?? '';
  const setAmount = (raw) => {
    patchUI({ amountDraft: raw });
    setOverrides({ ...overrides, amount: isValidAmount(raw) ? raw : null });
  };
  return (
    <Strip warning>
      {needWagering && (
        <>
          {choices.length > 0 && <span className="text-sm font-bold text-warning">Wagering</span>}
          {choices.map((m) => (
            <button
              key={m.tag} type="button" onClick={() => setOverrides({ ...overrides, wagering: m.wagering })}
              className={cn('rounded-md border px-2.5 py-1.5 text-xs font-bold',
                overrides.wagering === m.wagering ? 'border-warning bg-warning/20 text-warning' : 'border-white/10 bg-surface-2 text-ink-dim hover:text-ink')}
            >
              {m.tag} → {m.wagering}x
            </button>
          ))}
          {choices.length > 0 && <span className="text-sm font-medium text-ink-mute">or</span>}
          <NumberField
            id="comp-wagering" label={choices.length ? '' : 'Wagering'} placeholder="35" suffix="×"
            value={overrides.wagering ?? ''} onChange={(raw) => setOverrides({ ...overrides, wagering: parseWagering(raw) })}
          />
        </>
      )}
      {showAmount && (
        <NumberField
          id="comp-amount" label="Amount" placeholder="0.00000000" suffix={currency} width="w-40"
          value={amountDraft} onChange={setAmount} invalid={amountDraft !== '' && !isValidAmount(amountDraft)}
        />
      )}
      {showAmount && amountDraft !== '' && !isValidAmount(amountDraft) && (
        <span className="text-xs font-medium text-negative">Positive amount, max 8 decimals.</span>
      )}
      {ui.adjusting && (
        <button type="button" className="ml-auto text-sm font-bold text-ink-dim hover:text-ink"
          onClick={() => { patchUI({ adjusting: false, amountDraft: null }); setOverrides({ ...overrides, amount: null }); }}>
          Reset amount
        </button>
      )}
    </Strip>
  );
}

function GrantedStrip({ exception, setException }) {
  return (
    <Strip>
      <Check className="h-4 w-4 text-positive" />
      <span className="text-sm font-bold text-ink">One-off exception granted</span>
      <span className="min-w-0 flex-1 truncate text-sm font-medium text-ink-dim">“{exception.reason}”</span>
      <button type="button" className="text-sm font-bold text-ink-dim hover:text-ink" onClick={() => setException({ granted: false, reason: '' })}>Withdraw</button>
    </Strip>
  );
}

function ReasonStrip({ ui, patchUI, view }) {
  return (
    <div className="flex flex-col gap-2 border-t border-warning/30 bg-warning/5 px-6 py-4">
      <label htmlFor="exception-reason" className="text-sm font-bold text-warning">Reason for the exception (required)</label>
      <Textarea
        id="exception-reason" value={ui.reason || ''} maxLength={MAX_REASON_LENGTH} rows={2}
        placeholder="e.g. long-time VIP, on 35x wagering" onChange={(e) => patchUI({ reason: e.target.value })}
      />
      {ui.guidance && (
        <ul className="mt-1 flex flex-col gap-1.5">
          <li className="text-xs font-bold uppercase tracking-wider text-ink-mute">An exception fits when the player</li>
          {view.guidance.map((g) => (
            <li key={g.key} className={cn('flex items-center gap-2 text-sm font-medium', g.met ? 'text-ink' : 'text-ink-dim')}>
              <span className={cn('flex h-4 w-4 items-center justify-center rounded', g.met ? 'bg-positive/20 text-positive' : 'border border-white/15')}>
                {g.met && <Check className="h-3 w-3" />}
              </span>
              {g.text}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// CommentStrip: the saved comment with Edit (queued), or the comment box.
function CommentStrip({ check, view, detailed, ui, patchUI }) {
  const comment = commentOf(ui, check);
  const editing = ui.commentOpen || detailed;
  if (FINAL.includes(view.kind) || view.override) {
    return comment ? <Strip><span className="text-sm font-medium text-ink-dim">Comment</span><span className="text-sm font-bold text-ink">“{comment}”</span></Strip> : null;
  }
  if (!editing) {
    if (view.kind !== 'queued' || !comment) return null;
    return (
      <Strip>
        <span className="text-sm font-medium text-ink-dim">Comment</span>
        <span className="min-w-0 flex-1 whitespace-pre-line break-words text-sm font-bold text-ink">“{comment}”</span>
        <button type="button" className="text-sm font-bold text-ink-dim hover:text-ink" onClick={() => patchUI({ commentOpen: true })}>Edit</button>
      </Strip>
    );
  }
  return (
    <div className="flex flex-col gap-2 border-t border-white/10 px-6 py-4">
      <label htmlFor="comp-comment" className="text-xs font-medium text-ink-dim">Comment (optional)</label>
      <Textarea
        id="comp-comment" value={comment} maxLength={MAX_COMMENT_LENGTH} rows={2}
        placeholder="Visible to other VIP managers" onChange={(e) => patchUI({ comment: e.target.value })}
      />
    </div>
  );
}

// OverrideStrip: issuing a comp although a check failed. The form starts from the suggested comp
// (10% of deposits, at least 50) when there is one; the manager can change amount, currency and
// wagering, and the comment (why) is required; the failed checks are saved in front of it.
function OverrideStrip({ check, ui, patchUI }) {
  const form = ui.form;
  const periodNote = overridePeriodNote(check);
  const suggestion = !check.issued && overrideSuggestion(check);
  const set = (patch) => patchUI({ form: { ...form, ...patch } });
  const amountInvalid = form.amount !== '' && !isValidAmount(form.amount);
  return (
    <div className="flex flex-col gap-3 border-t border-warning/30 bg-warning/5 px-6 py-4">
      <p className="text-sm font-bold text-warning">Issue anyway: the failed checks are saved with the bonus, together with your comment.</p>
      {periodNote && <p className="text-xs font-medium text-ink-dim">{periodNote}</p>}
      {suggestion && <p className="text-xs font-medium text-ink-dim">{suggestion}</p>}
      <div className="flex flex-wrap items-center gap-4">
        <NumberField id="override-amount" label="Amount" placeholder="0.00" width="w-40" value={form.amount}
          onChange={(amount) => set({ amount })} invalid={amountInvalid} />
        <NumberField id="override-currency" label="Currency" placeholder="USD" width="w-24" value={form.currency}
          onChange={(currency) => set({ currency: currency.toUpperCase() })} />
        <NumberField id="override-wagering" label="Wagering" placeholder="35" suffix="×" value={form.wagering ?? ''}
          onChange={(raw) => set({ wagering: parseWagering(raw) })} />
      </div>
      <label htmlFor="override-comment" className="text-sm font-bold text-warning">Comment (required)</label>
      <Textarea
        id="override-comment" value={form.comment} maxLength={MAX_COMMENT_LENGTH} rows={2}
        placeholder="Why is this comp issued although the checks failed?" onChange={(e) => set({ comment: e.target.value })}
      />
    </div>
  );
}

// CardStrips: the middle of the result card for its kind.
export function CardStrips(props) {
  const { check, view, ui, exception, setException } = props;
  const cancelled = view.cancelled && (
    <Strip><span className="text-sm font-medium text-ink-dim">{view.cancelled}</span></Strip>
  );
  if (ui.form) return <>{cancelled}<OverrideStrip {...props} /></>;
  const editable = EDITABLE.includes(view.kind) && Boolean(check.proposal);
  return (
    <>
      {cancelled}
      {editable && view.wagering && !view.wagering.missing && <WageringLine wagering={view.wagering} />}
      {editable && <InputsStrip {...props} />}
      {view.kind === 'granted' && <GrantedStrip exception={exception} setException={setException} />}
      {view.kind === 'exception' && <ReasonStrip {...props} />}
      {(editable || FINAL.includes(view.kind) || view.override) && <CommentStrip {...props} />}
    </>
  );
}
